"use client";

import { lazy, Suspense, useMemo, useState } from "react";
import { toolHref } from "@/lib/tool-href";
import Link from "next/link";
import { ArrowRight, ArrowUpRight, Lock, Sparkles, Wand2 } from "lucide-react";
import type { Tool } from "@/types";

const ToolSearch = lazy(() =>
  import("./tool-search").then((m) => ({ default: m.ToolSearch }))
);

const SAMPLE = `{"name":"devstackio","local":true,"uploads":0}`;

function BenchDemo() {
  const [input, setInput] = useState(SAMPLE);
  const [output, setOutput] = useState("");
  const [note, setNote] = useState("Waiting for input");

  const run = () => {
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed, null, 2));
      setNote("Formatted on this device");
    } catch (e) {
      setOutput("");
      setNote(e instanceof Error ? e.message : "Could not parse that JSON");
    }
  };

  return (
    <div className="bench-face">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface-2)]/60 px-4 py-2.5">
        <p className="flex items-center gap-2 font-[family-name:var(--font-sans)] text-sm font-semibold text-[var(--color-text)]">
          <span className="signal-dot" aria-hidden="true" />
          Try it live — JSON formatter
        </p>
        <span className="bench-stamp">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Ran locally
        </span>
      </div>
      <div className="grid gap-0 md:grid-cols-2">
        <label className="block border-b border-[var(--color-border)] p-4 md:border-b-0 md:border-r">
          <span className="technical-label mb-2.5 block text-[var(--color-text-muted)]">Paste raw JSON</span>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={6}
            spellCheck={false}
            className="w-full border-0 bg-transparent p-0 font-[family-name:var(--font-mono)] text-sm shadow-none focus:shadow-none"
            aria-label="Raw JSON input for the live demo"
          />
        </label>
        <div className="bg-[#0F172A] p-4 md:rounded-br-[var(--radius-xl)]" aria-live="polite">
          <span className="technical-label mb-2.5 block text-[#94A3B8]">Result</span>
          {output ? (
            <pre className="max-h-44 overflow-auto border-0 bg-transparent p-0 text-sm" data-testid="tool-output">{output}</pre>
          ) : (
            <p className="text-sm leading-relaxed text-[#94A3B8]">Press “Shape it” to see the formatted result — nothing leaves this page.</p>
          )}
          <p className="mt-2.5 flex items-center gap-1.5 text-[13px] text-[#94A3B8]">
            <Sparkles className="h-3.5 w-3.5 text-[var(--color-signal)]" aria-hidden="true" />
            {note}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3">
        <p className="flex items-center gap-1.5 text-sm text-[var(--color-text-subtle)]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          Nothing leaves this page
        </p>
        <button
          type="button"
          onClick={run}
          className="inline-flex h-10 items-center gap-2 rounded-xl bg-[var(--color-accent-strong)] px-5 text-sm font-semibold text-[var(--color-accent-fg)] transition-colors hover:bg-[var(--color-accent-hover)] active:scale-[0.98]"
        >
          <Wand2 className="h-4 w-4" aria-hidden="true" />
          Shape it
        </button>
      </div>
    </div>
  );
}

export function Hero({ allTools }: { allTools: Tool[] }) {
  const trending = useMemo(
    () => [...allTools].sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0)).slice(0, 6),
    [allTools],
  );

  return (
    <section className="workbench-surface relative overflow-hidden border-b border-[var(--color-border)]">
      <div className="container relative pb-8 pt-8 md:pb-12 md:pt-10">
        <div className="bench-rail mb-6 animate-fade-in-down">
          <span className="signal-dot" aria-hidden="true" />
          <p><strong className="font-semibold text-[var(--color-text)]">Local mode</strong> — this workbench runs in your browser, uploads are off</p>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-10">
          <div className="max-w-2xl animate-fade-in-up">
            <h1
              className="font-[family-name:var(--font-display)] text-[clamp(2rem,4.4vw,3.4rem)] font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--color-text)] text-balance"
            >
              Free tools that keep your work{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span className="absolute inset-x-[-2%] bottom-[6%] top-[58%] -rotate-1 rounded-sm bg-[var(--color-signal)]" aria-hidden="true" />
                <em className="relative text-[var(--color-signal-ink)] font-[family-name:var(--font-display)] italic">on your machine</em>
              </span>
            </h1>

            <p className="lead mt-4 max-w-[52ch]">
              {allTools.length} tools for developers, students, and everyone else.
              Try the console on this page — formatting happens here, not on a server.
            </p>

            <div className="mt-5">
              <Suspense fallback={
                <div className="h-12 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]" />
              }>
                <ToolSearch allTools={allTools} />
              </Suspense>
            </div>

            <div className="mt-5 flex flex-wrap gap-2" aria-label="Trending tools">
              {trending.map((t) => (
                <Link
                  key={t.slug}
                  href={toolHref(t)}
                  className="group inline-flex h-10 items-center gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-medium text-[var(--color-text)] shadow-[var(--shadow-xs)] transition-colors hover:border-[var(--color-accent-strong)] hover:text-[var(--color-accent-strong)]"
                >
                  {t.name}
                  <ArrowUpRight className="h-3.5 w-3.5 text-[var(--color-text-subtle)] transition-colors group-hover:text-[var(--color-accent-strong)]" aria-hidden="true" />
                </Link>
              ))}
            </div>

            <dl className="mt-6 grid max-w-lg grid-cols-3 gap-4 border-t border-[var(--color-border)] pt-5">
              {[
                [`${allTools.length}+`, "free tools"],
                ["0", "uploads needed"],
                ["0", "accounts needed"],
              ].map(([v, l]) => (
                <div key={l}>
                  <dt className="sr-only">{l}</dt>
                  <dd className="font-[family-name:var(--font-display)] text-2xl font-semibold text-[var(--color-text)]">{v}</dd>
                  <dd className="mt-1 text-[13px] font-medium uppercase tracking-[0.06em] text-[var(--color-text-subtle)]">{l}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="animate-fade-in-up delay-2 lg:sticky lg:top-24">
            <BenchDemo />
            <Link
              href="/tools/json-formatter"
              className="group mt-4 flex items-center justify-between rounded-xl bg-[var(--color-accent-strong)] px-4 py-3 text-sm font-semibold text-[var(--color-accent-fg)] shadow-[var(--shadow-md)] transition-colors hover:bg-[var(--color-accent-hover)]"
            >
              <span>Open the full JSON formatter</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
